import {
  AutoFixHigh as GenerateIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { InputAdornment, TextField, Tooltip } from "@mui/material";
import DefaultLoader from "components/common/DefaultLoader";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import Toggle from "components/common/Toggle";
import * as config from "config";
import * as labels from "config.labels";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const useGeneratePolicyNumber = ({
  policyNumberConfig,
  onChange,
  layerId = undefined,
}) => {
  const [
    trigger,
    { isLoading, isError, error },
  ] = submissionsService.useGenerateNextPolicyNumberMutation();

  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );
  const priorSubmission = useSelector((state) =>
    submissionSelector.selectResolvedSubmissionWithoutInput(
      stateSelectors.selectPriorState(state)
    )
  );
  const activeLayerId = useSelector(pricingSelectors.selectResolvedActiveLayer)
    ?.id;

  const generatePolicyNumber = async (e) => {
    const forceRefresh = e?.shiftKey;
    const { data } = await trigger({
      submission,
      priorSubmission: forceRefresh ? null : priorSubmission,
      layerId: layerId ?? activeLayerId,
      config: policyNumberConfig,
    });
    if (data?.policyNumber) {
      onChange(data.policyNumber);
    }
  };

  return {
    generatePolicyNumber,
    isGenerating: isLoading,
    error: isError
      ? error?.name === "POLICY_NUMBER_ERROR" && error?.description
        ? error.description
        : `Unknown error. Please contact ${config.SUPPORT_EMAIL}.`
      : null,
  };
};

const ConditionalTooltip = ({
  children,
  tag,
  title,
  defaultToggle = false,
}) => {
  return (
    <Toggle
      byTag={tag}
      defaultToggle={defaultToggle}
      trueChild={
        <Tooltip title={title}>
          <span>{children}</span>
        </Tooltip>
      }
      falseChild={children}
    />
  );
};

const PolicyNumberField = ({
  value,
  disabled,
  generateLocked,
  onChange,
  label,
  type,
  layerId = undefined,
  ...props
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const {
    data: rawPolicyNumberConfig,
    isLoading: isConfigLoading,
  } = staticDataService.useConfigQuery("policy_numbers");

  const policyNumberConfig = {
    ...rawPolicyNumberConfig,
    generatorName: type
      ? rawPolicyNumberConfig?.generatorNameByType?.[type]
      : rawPolicyNumberConfig?.generatorName,
  };

  const {
    generatePolicyNumber,
    isGenerating,
    error,
  } = useGeneratePolicyNumber({ policyNumberConfig, onChange, layerId });

  const deletePolicyNumber = () => {
    onChange(null);
  };

  return (
    <Disabled ifReadOnly>
      <TextField
        {...props}
        type={"text"}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: !!value ? (
            <InputAdornment position={"end"}>
              <Disabled ifReadOnly>
                <IconButton
                  icon={ClearIcon}
                  edge={"end"}
                  onClick={deletePolicyNumber}
                  disabled={disabled}
                />
              </Disabled>
            </InputAdornment>
          ) : isConfigLoading ? (
            <></>
          ) : isGenerating ? (
            <DefaultLoader color={"#000000"} />
          ) : (
            <InputAdornment position={"end"}>
              <ConditionalTooltip
                tag={"generatePolicyNumber.tooltip"}
                title={translateLabel(labels.GENERATE_POLICY_NUMBER_TOOLTIP, {
                  default:
                    "Hold the SHIFT key to ignore any prior submission and generate a brand new reference",
                })}
                defaultToggle={false}
              >
                <Disabled ifReadOnly>
                  <IconButton
                    icon={GenerateIcon}
                    edge={"end"}
                    onClick={generatePolicyNumber}
                    disabled={disabled || generateLocked}
                  />
                </Disabled>
              </ConditionalTooltip>
            </InputAdornment>
          ),
        }}
        label={label ?? (props.hiddenLabel ? null : "Reference")}
        variant={"filled"}
        onChange={(e) => {
          let value = e.target.value;
          if (!value || value === "") {
            value = null;
          }
          onChange(value);
        }}
        value={value ?? ""}
        disabled={disabled}
        error={!!error}
        helperText={error}
        fullWidth
      />
    </Disabled>
  );
};

export default PolicyNumberField;
